import { type DeepPartial } from './types';

// Define a comprehensive theme configuration interface
export type ThemeConfig = {
  colors: {
    // Base color palette
    primary: {
      DEFAULT: string;
      foreground: string;
      hover: string;
    };
    secondary: {
      DEFAULT: string;
      foreground: string;
      hover: string;
    };
    accent: {
      DEFAULT: string;
      foreground: string;
    };
    
    // Semantic colors
    background: {
      DEFAULT: string;
      secondary: string;
    };
    foreground: {
      DEFAULT: string;
      muted: string;
    };
    
    // Component-specific colors
    topbar: {
      background: string;
      foreground: string;
      hover: string;
      border: string;
    };
    
    // State colors
    success: {
      DEFAULT: string;
      foreground: string;
    };
    warning: {
      DEFAULT: string;
      foreground: string;
    };
    danger: {
      DEFAULT: string;
      foreground: string;
    };
  };
  
  // Typography
  typography: {
    fontFamily: string;
    fontSize: {
      base: string;
      sm: string;
      lg: string;
      xl: string;
    };
  };
  
  // Spacing and sizing
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  
  // Border and radius
  borderRadius: {
    sm: string;
    md: string;
    lg: string;
    full: string;
  };
}

// Default theme configuration
export const defaultTheme: ThemeConfig = {
  colors: {
    primary: {
      DEFAULT: 'var(--primary-default)',
      foreground: 'var(--background-secondary)',
      hover: '#1d4ed8',
    },
    secondary: {
      DEFAULT: '#4f46e5',
      foreground: 'var(--background-secondary)',
      hover: '#4338ca',
    },
    accent: {
      DEFAULT: '#0ea5e9',
      foreground: 'var(--background-secondary)',
    },
    background: {
      DEFAULT: 'var(--background-default)',
      secondary: 'var(--background-secondary)',
    },
    foreground: {
      DEFAULT: 'var(--foreground-default)',
      muted: 'var(--foreground-muted)',
    },
    topbar: {
      // Korvo dark topbar defaults
      background: '#0b0b0f',
      foreground: 'var(--background-muted)',
      hover: '#111217',
      border: '#151519',
    },
    success: {
      DEFAULT: 'var(--success-default)',
      foreground: 'var(--background-secondary)',
    },
    warning: {
      DEFAULT: 'var(--warning-default)',
      foreground: 'var(--background-secondary)',
    },
    danger: {
      DEFAULT: 'var(--danger-default)',
      foreground: 'var(--background-secondary)',
    },
  },
  typography: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: {
      base: '1rem',
      sm: '0.875rem',
      lg: '1.125rem',
      xl: '1.25rem',
    },
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  borderRadius: {
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    full: '9999px',
  },
};

// Theme application function with improved type safety
export function applyTheme(theme: DeepPartial<ThemeConfig> = {}) {
  const root = document.documentElement;
  
  // Deep merge of theme configurations
  const mergedTheme = deepMerge(defaultTheme, theme);
  
  // Variables that should be controlled by CSS dark mode, not inline styles
  const cssControlledVars = new Set([
    '--background-default',
    '--background-secondary',
    '--foreground-default',
    '--foreground-muted',
  ]);
  
  // Apply color variables (skip CSS-controlled variables)
  Object.entries(mergedTheme.colors).forEach(([category, colors]) => {
    if (typeof colors === 'object') {
      Object.entries(colors).forEach(([key, value]) => {
        if (typeof value === 'string') {
          // Convert DEFAULT to default, camelCase to kebab-case
          const normalizedKey = key === 'DEFAULT' ? 'default' : key.replace(/([A-Z])/g, '-$1').toLowerCase();
          const cssVariableName = `--${category}-${normalizedKey}`;
          // Skip variables that are controlled by CSS dark mode
          if (!cssControlledVars.has(cssVariableName)) {
            root.style.setProperty(cssVariableName, value);
          }
        }
      });
    }
  });
  
  // Apply typography variables
  Object.entries(mergedTheme.typography).forEach(([key, value]) => {
    if (typeof value === 'string') {
      root.style.setProperty(
        `--typography-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, 
        value
      );
    } else if (isPlainObject(value)) {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        if (typeof nestedValue === 'string') {
          root.style.setProperty(
            `--typography-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}-${nestedKey.replace(/([A-Z])/g, '-$1').toLowerCase()}`,
            nestedValue
          );
        }
      });
    }
  });
  
  // Apply spacing variables
  Object.entries(mergedTheme.spacing).forEach(([key, value]) => {
    root.style.setProperty(
      `--spacing-${key}`, 
      value
    );
  });
  
  // Apply border radius variables
  Object.entries(mergedTheme.borderRadius).forEach(([key, value]) => {
    root.style.setProperty(
      `--border-radius-${key}`, 
      value
    );
  });
}

// Utility function for deep merging objects
function deepMerge<T extends Record<string, unknown>>(target: T, source: DeepPartial<T>): T {
  const output: Record<string, unknown> = { ...target };
  
  if (isPlainObject(target) && isPlainObject(source)) {
    (Object.keys(source) as Array<keyof T>).forEach((key) => {
      const sourceValue = source[key];
      if (isPlainObject(sourceValue)) {
        const targetValue = target[key];
        if (targetValue === undefined) {
          output[String(key)] = sourceValue as T[typeof key];
        } else if (isPlainObject(targetValue)) {
          output[String(key)] = deepMerge(
            targetValue as Record<string, unknown>,
            sourceValue as DeepPartial<Record<string, unknown>>,
          );
        } else {
          output[String(key)] = sourceValue as T[typeof key];
        }
      } else if (sourceValue !== undefined) {
        output[String(key)] = sourceValue as T[typeof key];
      }
    });
  }
  
  return output as T;
}

// Type guard for object check
function isPlainObject(item: unknown): item is Record<string, unknown> {
  return typeof item === 'object' && item !== null && !Array.isArray(item);
}

// Theme toggle and initialization functions
export function toggleDarkMode() {
  if (typeof document === 'undefined') return;

  document.documentElement.classList.toggle('dark');
  localStorage.setItem('theme', 
    document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  );
}

export function initializeDarkMode() {
  if (typeof document === 'undefined') return;
  const savedTheme = localStorage.getItem('theme');
  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}
